import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  MenuItem,
  Paper,
  Typography,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  generateUid,
  roundTo15,
  parseFirstVevent,
  stripTime,
  addMinutes,
  clampInt,
  combineDateAndTime,
  sortWeekdayTokens,
  freqStringToNumber,
  freqNumberToString,
  tokenToShort,
  formatHHMM,
  toIcalLocal,
} from "./ICalUtils";

import * as ETVConstants from "../ETVConstants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { RRule } from "rrule";

/* ------------------------------------------------------------- */

const WEEKDAYS = [
  { label: "mon", token: "MO" },
  { label: "tue", token: "TU" },
  { label: "wed", token: "WE" },
  { label: "thu", token: "TH" },
  { label: "fri", token: "FR" },
  { label: "sat", token: "SA" },
  { label: "sun", token: "SU" },
];

const TOKEN_BY_JS_DAY = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
const VALID_TOKENS = new Set(TOKEN_BY_JS_DAY);

const getOrdinalForDayInMonth = (d) => Math.ceil(d / 7);
const isLastWeekdayOccurrenceInMonth = (date) => {
  const d = date.getDate();
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  return d > lastDay - 7;
};

/* ------------------------------------------------------------- */

export default function ICalEventEditor({
  filename = "",
  thumbnail = null,
  initialIcs = "",
  prodId = "-//elevator.tv//contentplanner//EN",
  onChange,
}) {
  const lastParsedRef = useRef("");
  const [error, setError] = useState("");
  const [uid] = useState(generateUid());
  const parsingRef = useRef(false);

  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /* ---------- valid tags ---------- */
  const [validationMsg, setValidationMsg] = useState("");
  const [isValid, setIsValid] = useState(true);

  /* ---------- base date/time ---------- */
  const nowRounded = roundTo15(new Date());
  const [anchorDate, setAnchorDate] = useState(stripTime(nowRounded));
  const [endDate, setEndDate] = useState(stripTime(addMinutes(nowRounded, 120)));
  const [startTime, setStartTime] = useState(nowRounded);
  const [endTime, setEndTime] = useState(addMinutes(nowRounded, 120));

  /* ---------- recurrence ---------- */
  const [isRecurring, setIsRecurring] = useState(false);
  const [freq, setFreq] = useState("DAILY"); // DAILY/WEEKLY/MONTHLY/YEARLY
  const [interval, setInterval] = useState(1);

  // WEEKLY
  const [byWeekday, setByWeekday] = useState([]); // ["MO","WE"]

  // MONTHLY
  const [monthlyMode, setMonthlyMode] = useState("monthday"); // "monthday" | "weekday"
  const [monthDay, setMonthDay] = useState(1); // 1..31
  const [monthNth, setMonthNth] = useState(1); // 1..4 or -1
  const [monthWeekday, setMonthWeekday] = useState("MO"); // "MO".."SU"

  // ENDS
  const [endsMode, setEndsMode] = useState("never"); // "never" | "count" | "until"
  const [count, setCount] = useState(5);

  /* ------------------------------------------------------------- */
  /* Combine start/end */

  const dtStart = useMemo(
    () => combineDateAndTime(anchorDate, startTime),
    [anchorDate, startTime]
  );

  const dtEnd = useMemo(() => {
    let end = combineDateAndTime(endDate, endTime);
    if (end <= dtStart) end = addMinutes(dtStart, 15);
    return end;
  }, [endDate, endTime, dtStart]);

  const durationMs = useMemo(() => {
    const d = dtEnd.getTime() - dtStart.getTime();
    return Math.max(15 * 60 * 1000, d);
  }, [dtStart, dtEnd]);  

  const endOfDay = (d) => {
    const x = new Date(d);
    x.setHours(23, 59, 59, 0);
    return x;
  };
  const toIcalLocalNoZ = (date) => toIcalLocal(date); // just alias for clarity  

  // For recurring events: one occurrence should NOT span multiple days.
  // We interpret endTime as the end time on the SAME day as dtStart.
  const dtEndRecurring = useMemo(() => {
    let endSameDay = combineDateAndTime(anchorDate, endTime);
    // if user picked an end time <= start time, keep it invalid (UX check),
    // but still guard against zero/negative duration to avoid crashes:
    if (endSameDay <= dtStart) endSameDay = addMinutes(dtStart, 15);
    return endSameDay;
  }, [anchorDate, endTime, dtStart]);

  
  useEffect(() => {
    let ok = true;
    let msg = "";
    if (isRecurring) {
      const sMin = dtStart.getHours() * 60 + dtStart.getMinutes();
      const e = isRecurring ? dtEndRecurring : dtEnd;
      const eMin = e.getHours() * 60 + e.getMinutes();
      // Better UX: for recurring rules, keep the daily time window "normal"
      // (no wrap-across-midnight within a single recurrence day).
      if (eMin <= sMin) {
        ok = false;
        msg =
          freq === "DAILY"
            ? "in daily mode, end time must be later than start time"
            : freq === "WEEKLY"
            ? "in weekly mode, end time must be later than start time"
            : "end time must be later than start time";
      }
    }
    setIsValid(ok);
    setValidationMsg(msg);
  }, [isRecurring, freq, dtStart, dtEnd, dtEndRecurring]);

  /* ------------------------------------------------------------- */
  /* Keep MONTHLY defaults in sync with selected date */

  useEffect(() => {
    // While we are parsing an existing RRULE, don't overwrite what we parsed.
    if (parsingRef.current) return;
    const d = clampInt(dtStart.getDate(), 1, 31);
    setMonthDay(d);
    const token = TOKEN_BY_JS_DAY[dtStart.getDay()] || "MO";
    setMonthWeekday(token);
    const nth = isLastWeekdayOccurrenceInMonth(dtStart) ? -1 : getOrdinalForDayInMonth(d);
    setMonthNth(nth);
  }, [dtStart]);

  /* ------------------------------------------------------------- */
  /* Clear weekly picks when leaving WEEKLY */

  useEffect(() => {
    if (freq !== "WEEKLY" && byWeekday.length) setByWeekday([]);
    // monthly settings remain; they are only used when MONTHLY
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [freq]);

  /* ------------------------------------------------------------- */
  /* Parse existing ICS */

  useEffect(() => {
    const trimmed = (initialIcs || "").trim();
    if (!trimmed) return;
    if (trimmed === lastParsedRef.current) return;
    lastParsedRef.current = trimmed;
    try {
      setError("");
      parsingRef.current = true;
      const parsed = parseFirstVevent(trimmed);
      if (!parsed) return;
      if (parsed.dtStart) {
        setAnchorDate(stripTime(parsed.dtStart));
        setStartTime(parsed.dtStart);
      }
      if (parsed.dtEnd) {
        setEndDate(stripTime(parsed.dtEnd));
        setEndTime(parsed.dtEnd);
      } else if (parsed.dtStart) {
        setEndDate(stripTime(parsed.dtStart));
      }
      if (!parsed.rrule) {
        setIsRecurring(false);
        setFreq("DAILY");
        setInterval(1);
        setByWeekday([]);
        setEndsMode("never");
        return;
      }
      setIsRecurring(true);    
      const ro = parsed.rrule.options || {};     
      setFreq(freqNumberToString(ro.freq));
      setInterval(ro.interval || 1);
      // ends
      if (typeof ro.count === "number" && ro.count > 0) {
        setEndsMode("count");
        setCount(ro.count);
      } else if (ro.until) {
        setEndsMode("until");
        setEndDate(stripTime(ro.until)); // recurrence end date
      } else {
        setEndsMode("never");
      }
      // weekly tokens (prefer parseFirstVevent normalized tokens)
      if (Array.isArray(parsed.byWeekdayTokens) && parsed.byWeekdayTokens.length) {
        setByWeekday(parsed.byWeekdayTokens.filter((t) => VALID_TOKENS.has(t)));
      } else if (Array.isArray(ro.byweekday) && ro.byweekday.length) {
        const tokens = ro.byweekday
          .map((w) => String(w).substring(0, 2))
          .filter((t) => VALID_TOKENS.has(t));
        setByWeekday(tokens);
      } else {
        setByWeekday([]);
      }
      // monthly detection + load monthly params if present
      if (ro.freq === RRule.MONTHLY) {
        const rr = String(parsed.rruleString || "").toUpperCase().replace(/\s+/g, "");
        // 1) Day-of-month rules: BYMONTHDAY=22 (or BYMONTHDAY=22,23 etc.)
        const mdMatch = rr.match(/BYMONTHDAY=([0-9]{1,2})/);
        if (mdMatch) {
          setMonthlyMode("monthday");
          setMonthDay(clampInt(Number(mdMatch[1]), 1, 31));
        } else {
          // 2) Nth-weekday in compact form: BYDAY=+2TU or BYDAY=-1WE
          const nthDayMatch = rr.match(/BYDAY=([+-]?\d+)(MO|TU|WE|TH|FR|SA|SU)/);
          // 3) Nth-weekday in split form: BYDAY=WE;BYSETPOS=-1
          const daySetposMatch = rr.match(/BYDAY=(MO|TU|WE|TH|FR|SA|SU).*BYSETPOS=([+-]?\d+)/);
          let token = null;
          let nth = null;
          if (nthDayMatch) {
            nth = Number(nthDayMatch[1]);
            token = nthDayMatch[2];
          } else if (daySetposMatch) {
            token = daySetposMatch[1];
            nth = Number(daySetposMatch[2]);
          }
          if (token && Number.isFinite(nth)) {
            setMonthlyMode("weekday");
            setMonthWeekday(token);
            if (![1, 2, 3, 4, -1].includes(nth)) nth = 1;
            setMonthNth(nth);
          } else {
            // fallback: if nothing detected, keep as monthday based on DTSTART date
            setMonthlyMode("monthday");
            setMonthDay(clampInt(parsed.dtStart?.getDate?.() ?? dtStart.getDate(), 1, 31));
          }
        }
      }
      setTimeout(() => {
        parsingRef.current = false;
      }, 0);

    } catch (e) {
      setTimeout(() => {
        parsingRef.current = false;
      }, 0);
      setError(String(e?.message || e));
    }
  }, [initialIcs]);

  /* ------------------------------------------------------------- */
  /* RRULE builder */

  const rruleString = useMemo(() => {
    if (!isRecurring) return "";
    try {
      setError("");

      const options = {
        freq: freqStringToNumber(freq), // DAILY/WEEKLY/MONTHLY/YEARLY
        interval: clampInt(interval, 1, 999),
        dtstart: dtStart,
        // ❌ DO NOT set "until" here by default
      };

      // WEEKLY: default to dtStart weekday if nothing selected
      if (freq === "WEEKLY") {
        const tokens = byWeekday.length ? byWeekday : [TOKEN_BY_JS_DAY[dtStart.getDay()]];
        options.byweekday = tokens.filter((t) => VALID_TOKENS.has(t)).map((t) => RRule[t]);
      }

      // MONTHLY:
      if (freq === "MONTHLY") {
        if (monthlyMode === "monthday") {
          options.bymonthday = [clampInt(monthDay, 1, 31)];
        } else {
          const nth = [1, 2, 3, 4, -1].includes(monthNth) ? monthNth : 1;
          const token = VALID_TOKENS.has(monthWeekday) ? monthWeekday : "MO";
          options.byweekday = [RRule[token].nth(nth)];
        }
      }

      // ENDS
      if (endsMode === "count") {
        options.count = clampInt(count, 1, 9999);
      } else if (endsMode === "until") {
        options.until = endOfDay(endDate);
      } else {
        // "never"
        delete options.count;
        delete options.until;
      }

      const rule = new RRule(options);
      const s = rule.toString();
      const line = s.split("\n").find((l) => l.startsWith("RRULE:"));
      return line ? line.replace("RRULE:", "") : "";
    } catch (e) {
      console.warn("invalid recurrence", e);
      setError("invalid recurrence");
      return "";
    }
  }, [
    isRecurring,
    freq,
    interval,
    byWeekday,
    monthlyMode,
    monthDay,
    monthNth,
    monthWeekday,
    endsMode,
    count,
    dtStart,
    endDate,
  ]);


  /* ------------------------------------------------------------- */
  /* ICS generator (LOCAL FLOATING) */

  const ics = useMemo(() => {
    if (error) return "";
    try {
      const lines = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        `PRODID:${prodId}`,
        "BEGIN:VEVENT",
        `UID:${uid}`,
        `DTSTAMP:${toIcalLocal(new Date())}`,
        `SUMMARY:${filename || "content"}`,
        `DTSTART:${toIcalLocal(dtStart)}`,
        `DTEND:${toIcalLocal(isRecurring ? dtEndRecurring : dtEnd)}`
      ];
      if (isRecurring && rruleString) lines.push(`RRULE:${rruleString}`);
      lines.push("END:VEVENT");
      lines.push("END:VCALENDAR");
      return lines.join("\r\n");
    } catch (e) {
      setError(String(e?.message || e));
      return "";
    }
  }, [uid, filename, prodId, dtStart, dtEnd, dtEndRecurring, isRecurring, rruleString, error]);

  /* ------------------------------------------------------------- */
  /* Emit */

  useEffect(() => {
    const cb = onChangeRef.current;
    if (!cb) return;
    const emittedDtEnd = isRecurring ? dtEndRecurring : dtEnd;
    
    // ✅ debug: log produced ICS + key fields
    /*
    console.log("[ICalEventEditor] produced ICS", {
      isRecurring,
      freq,
      endsMode,
      count,
      dtStart: dtStart?.toString?.(),
      dtEnd: emittedDtEnd?.toString?.(),
      rruleString,
      ics,
    });
    */
  
    cb({
      ics,
      event: {
        dtStart,
        dtEnd: emittedDtEnd,
        rrule: rruleString,
        isValid,
        validationMsg,
      },
    });
  }, [
    ics,
    dtStart,
    dtEnd,
    dtEndRecurring,
    rruleString,
    isRecurring,
    freq,
    endsMode,
    count,
    isValid,
    validationMsg,
  ]);

  /* ------------------------------------------------------------- */
  /* UI summary */

  const summary = useMemo(() => {
    if (!isRecurring) {
      const s = `${anchorDate.toLocaleDateString()} ${formatHHMM(dtStart)}`;
      const e = `${endDate.toLocaleDateString()} ${formatHHMM(dtEnd)}`;
      return `${s} → ${e}`;
    }
    const endForDisplay = isRecurring ? dtEndRecurring : dtEnd;
    const timePart = `${formatHHMM(dtStart)}–${formatHHMM(endForDisplay)}`
    let text = `every ${interval > 1 ? interval + " " : ""}${freq.toLowerCase()}`;
    if (freq === "WEEKLY") {
      const tokens = byWeekday.length ? byWeekday : [TOKEN_BY_JS_DAY[dtStart.getDay()]];
      text += ` on ${sortWeekdayTokens(tokens).map(tokenToShort).join(", ")}`;
    }
    if (freq === "MONTHLY") {
      if (monthlyMode === "monthday") {
        text += ` on day ${clampInt(monthDay, 1, 31)}`;
      } else {
        const nthLabel =
          monthNth === -1 ? "last" : ["", "1st", "2nd", "3rd", "4th"][monthNth] || "1st";
        text += ` on the ${nthLabel} ${tokenToShort(monthWeekday)}`;
      }
    }
    text += ` ${timePart}`;
    if (endsMode === "count") text += ` for ${count} times`;
    return text;
  }, [
    isRecurring,
    freq,
    interval,
    byWeekday,
    monthlyMode,
    monthDay,
    monthNth,
    monthWeekday,
    dtStart,
    dtEnd,
    anchorDate,
    endDate,
    endsMode,
    count,
  ]);

  /* ------------------------------------------------------------- */
  /* UI */

  return (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        maxWidth: 720,
        mx: "auto",
      }}
    >
      <Stack spacing={1.5}>
        {error && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {error}
          </Alert>
        )}
        {validationMsg ? (
          <Alert severity="error" sx={{ py: 0.5 }}>
            {validationMsg}
          </Alert>
        ) : null}

        {/* Content header: thumbnail + filename */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
          {thumbnail ? (
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
              <Box
                component="img"
                src={thumbnail}
                alt=""
                sx={{
                  width: 96,
                  height: 96,
                  borderRadius: 3,
                  objectFit: "cover",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.18)",
                  flexShrink: 0,
                }}
              />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {filename}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {ETVConstants.trans("schedule_settings")}
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 1.5,
                bgcolor: "action.hover",
                flexShrink: 0,
              }}
            />
          )}
        </Stack>

        {/* Start/End rows + recurrence (grid to avoid wrap surprises) */}
        <Box sx={{ mt: 2.5 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "170px 140px 1fr auto", // date | time | spacer | switch
              columnGap: 2,
              rowGap: 2,
              alignItems: "center",
            }}
          >
            {/* Row 1: Start date/time */}
            <DatePicker
              label={ETVConstants.trans("start_date") || "start date"}
              value={anchorDate}
              onChange={(v) => {
                if (!v) return;
                const newStartDate = stripTime(v);
                const newStart = combineDateAndTime(newStartDate, startTime);
                const newEnd = new Date(newStart.getTime() + durationMs);
                setAnchorDate(newStartDate);
                if (!isRecurring) {
                  setEndDate(stripTime(newEnd));
                  setEndTime(newEnd);
                }
              }}
              slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
            />

            <TimePicker
              label={ETVConstants.trans("start_time") || "start time"}
              value={startTime}
              onChange={(v) => {
                if (!v) return;
                const t = roundTo15(v);
                const newStart = combineDateAndTime(anchorDate, t);
                const newEnd = new Date(newStart.getTime() + durationMs);
                setStartTime(t);
                if (!isRecurring) {
                  setEndDate(stripTime(newEnd));
                  setEndTime(newEnd);
                }
              }}
              ampm={false}
              slotProps={{ textField: { size: "small", sx: { width: 140 } } }}
            />

            <Box />
            <Box />

            {/* Row 2: End/Until date (optional) */}
            {(!isRecurring || endsMode === "until") ? (
              <DatePicker
                label={
                  isRecurring
                    ? ETVConstants.trans("until") || "until date"
                    : ETVConstants.trans("end_date") || "end date"
                }
                value={endDate}
                onChange={(v) => {
                  if (!v) return;

                  if (isRecurring) {
                    // until date only
                    setEndDate(stripTime(v));
                  } else {
                    // non-recurring end date influences dtEnd
                    const d = stripTime(v);
                    let e = combineDateAndTime(d, endTime);
                    if (e <= dtStart) e = addMinutes(dtStart, 15);
                    setEndDate(d);
                    setEndTime(e);
                  }
                }}
                slotProps={{ textField: { size: "small", sx: { minWidth: 170 } } }}
              />
            ) : (
              <Box />
            )}

            {/* Row 2: End time (always shown, stays under start time) */}
            <TimePicker
              label={ETVConstants.trans("end_time") || "end time"}
              value={endTime}
              onChange={(v) => {
                if (!v) return;
                const t = roundTo15(v);

                if (isRecurring) {
                  setEndTime(t);
                  return;
                }

                let e = combineDateAndTime(endDate, t);
                if (e <= dtStart) e = addMinutes(dtStart, 15);
                setEndTime(e);
              }}
              ampm={false}
              slotProps={{ textField: { size: "small", sx: { width: 140 } } }}
            />

            <Box />

            <FormControlLabel
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontWeight: 600, fontSize: 14 },
              }}
              control={
                <Switch
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  sx={{ transform: "scale(1.15)" }}
                />
              }
              label={ETVConstants.trans("recurrence")}
            />
          </Box>
        </Box>

        <Divider />

        {isRecurring && (
          <Stack spacing={1.2}>
            {/* Row: frequency + interval + ends (+count) */}
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                select
                label={ETVConstants.trans("frequency")}
                size="small"
                value={freq}
                onChange={(e) => setFreq(e.target.value)}
                sx={{ minWidth: 170 }}
              >
                <MenuItem value="DAILY">{ETVConstants.trans("daily")}</MenuItem>
                <MenuItem value="WEEKLY">{ETVConstants.trans("weekly")}</MenuItem>
                <MenuItem value="MONTHLY">{ETVConstants.trans("monthly")}</MenuItem>
                <MenuItem value="YEARLY">{ETVConstants.trans("yearly")}</MenuItem>
              </TextField>

              <TextField
                label={ETVConstants.trans("interval")}
                size="small"
                type="number"
                value={interval}
                onChange={(e) => setInterval(clampInt(Number(e.target.value), 1, 999))}
                sx={{ width: 110 }}
                inputProps={{ min: 1, max: 999 }}
              />

              <TextField
                select
                label={ETVConstants.trans("ends")}
                size="small"
                value={endsMode}
                onChange={(e) => setEndsMode(e.target.value)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="never">{ETVConstants.trans("never")}</MenuItem>
                <MenuItem value="until">{ETVConstants.trans("until")}</MenuItem>
                <MenuItem value="count">{ETVConstants.trans("after_N")}</MenuItem>
              </TextField>

              {endsMode === "count" && (
                <TextField
                  label={ETVConstants.trans("count")}
                  size="small"
                  type="number"
                  value={count}
                  onChange={(e) => setCount(clampInt(Number(e.target.value), 1, 9999))}
                  sx={{ width: 110 }}
                  inputProps={{ min: 1, max: 9999 }}
                />
              )}
            </Stack>

            {/* WEEKLY */}
            {freq === "WEEKLY" && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                {WEEKDAYS.map((d) => (
                  <ToggleButton
                    key={d.token}
                    value={d.token}
                    selected={byWeekday.includes(d.token)}
                    onChange={() => {
                      const next = new Set(byWeekday);
                      next.has(d.token) ? next.delete(d.token) : next.add(d.token);
                      setByWeekday(Array.from(next));
                    }}
                    size="small"
                    sx={{
                      height: 34,
                      px: 1.4,
                      borderRadius: 2,
                      textTransform: "none",
                      fontSize: 13,
                      fontWeight: 600,
                      "&.Mui-selected": { backgroundColor: "#4caf50", color: "#fff" },
                      "&.Mui-selected:hover": { backgroundColor: "#43a047" },
                    }}
                  >
                    {ETVConstants.trans(d.label)}
                  </ToggleButton>
                ))}
              </Box>
            )}

            {/* MONTHLY */}
            {freq === "MONTHLY" && (
              <Stack spacing={1}>
                <ToggleButtonGroup
                  exclusive
                  value={monthlyMode}
                  onChange={(e, v) => v && setMonthlyMode(v)}
                  size="small"
                  sx={{
                    "& .MuiToggleButton-root": {
                      height: 36,
                      px: 2,
                      fontSize: 13,
                      textTransform: "none",
                    },
                  }}
                >
                  <ToggleButton value="monthday">{ETVConstants.trans("on_day")}</ToggleButton>
                  <ToggleButton value="weekday">{ETVConstants.trans("on_the_")}</ToggleButton>
                </ToggleButtonGroup>

                {monthlyMode === "monthday" ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ minWidth: 56, fontWeight: 600 }}>
                      {ETVConstants.trans("on_day")}
                    </Typography>

                    <TextField
                      size="small"
                      type="number"
                      value={monthDay}
                      onChange={(e) => setMonthDay(clampInt(Number(e.target.value), 1, 31))}
                      sx={{ width: 120 }}
                      inputProps={{ min: 1, max: 31 }}
                    />
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" sx={{ minWidth: 56, fontWeight: 600 }}>
                      {ETVConstants.trans("on_the_")}
                    </Typography>

                    <TextField
                      select
                      size="small"
                      value={String(monthNth)}
                      onChange={(e) => setMonthNth(Number(e.target.value))}
                      sx={{ width: 120 }}
                    >
                      <MenuItem value="1">{ETVConstants.trans("1st")}</MenuItem>
                      <MenuItem value="2">{ETVConstants.trans("2nd")}</MenuItem>
                      <MenuItem value="3">{ETVConstants.trans("3rd")}</MenuItem>
                      <MenuItem value="4">{ETVConstants.trans("4th")}</MenuItem>
                      <MenuItem value="-1">{ETVConstants.trans("last")}</MenuItem>
                    </TextField>

                    <TextField
                      select
                      size="small"
                      value={monthWeekday}
                      onChange={(e) => setMonthWeekday(e.target.value)}
                      sx={{ width: 140 }}
                    >
                      {WEEKDAYS.map((d) => (
                        <MenuItem key={d.token} value={d.token}>
                          {ETVConstants.trans(d.label)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        )}

        {/* Footer summary */}
        <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
          <Box
            sx={{
              px: 1,
              py: 0.6,
              borderRadius: 2,
              bgcolor: "action.hover",
              fontSize: 12,
              color: "text.secondary",
            }}
          >
            {summary}
          </Box>
        </Box>
      </Stack>
    </Paper>
  </LocalizationProvider>
);

}